import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Alert as MuiAlert,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CheckCircle as AcknowledgeIcon,
  Check as ResolveIcon,
  Cancel as DismissIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import {
  Alert,
  AlertSummary,
  AlertFilters,
  generateAlerts,
  getAlerts,
  getAlertsSummary,
  acknowledgeAlert,
  resolveAlert,
  dismissAlert,
} from '../services/alertsService';

const AlertsDashboard: React.FC = () => {
  const { user } = useAuth();
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [summary, setSummary] = useState<AlertSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<AlertFilters>({ status: 'open', limit: 50 });
  const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null);
  const [actionDialog, setActionDialog] = useState<'acknowledge' | 'resolve' | 'dismiss' | null>(null);
  const [actionNotes, setActionNotes] = useState('');
  const [assignTo, setAssignTo] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const loadAlerts = useCallback(async () => {
    try {
      setLoading(true);
      const [alertsData, summaryData] = await Promise.all([
        getAlerts(filters),
        getAlertsSummary(),
      ]);
      setAlerts(alertsData.alerts || []);
      setSummary(summaryData);
      setError(null);
    } catch (err: any) {
      setError(`Failed to load alerts: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadAlerts();
  }, [loadAlerts]);

  const handleGenerateAlerts = async () => {
    try {
      setLoading(true);
      const result = await generateAlerts();
      setSuccess(`Generated ${result.alerts_created} new alerts`);
      loadAlerts();
    } catch (err: any) {
      setError(`Failed to generate alerts: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledge = async () => {
    if (!selectedAlert || !user?.staff_no) return;
    try {
      await acknowledgeAlert(selectedAlert.id, user.staff_no, assignTo || undefined);
      setSuccess('Alert acknowledged');
      setActionDialog(null);
      setAssignTo('');
      loadAlerts();
    } catch (err: any) {
      setError(`Failed to acknowledge: ${err.message}`);
    }
  };

  const handleResolve = async () => {
    if (!selectedAlert || !user?.staff_no || !actionNotes.trim()) return;
    try {
      await resolveAlert(selectedAlert.id, user.staff_no, actionNotes);
      setSuccess('Alert resolved');
      setActionDialog(null);
      setActionNotes('');
      loadAlerts();
    } catch (err: any) {
      setError(`Failed to resolve: ${err.message}`);
    }
  };

  const handleDismiss = async () => {
    if (!selectedAlert || !user?.staff_no || !actionNotes.trim()) return;
    try {
      await dismissAlert(selectedAlert.id, user.staff_no, actionNotes);
      setSuccess('Alert dismissed');
      setActionDialog(null);
      setActionNotes('');
      loadAlerts();
    } catch (err: any) {
      setError(`Failed to dismiss: ${err.message}`);
    }
  };

  const getSeverityColor = (severity: string): "error" | "warning" | "info" | "success" => {
    switch (severity) {
      case 'critical': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'success';
      default: return 'info';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical': return <ErrorIcon />;
      case 'high': return <WarningIcon />;
      default: return <InfoIcon />;
    }
  };

  const getAlertTypeLabel = (type: string) => {
    return type.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        PO Alerts Dashboard
      </Typography>

      {error && (
        <MuiAlert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </MuiAlert>
      )}

      {success && (
        <MuiAlert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </MuiAlert>
      )}

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Open Alerts</Typography>
                <Typography variant="h4">{summary.total_open}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Critical</Typography>
                <Typography variant="h4" color="error">{summary.critical_count}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>High Priority</Typography>
                <Typography variant="h4" color="warning.main">{summary.high_count}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Acknowledged</Typography>
                <Typography variant="h4">{summary.total_acknowledged}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Controls */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status || ''}
                label="Status"
                onChange={(e) => setFilters({ ...filters, status: e.target.value as any })}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="open">Open</MenuItem>
                <MenuItem value="acknowledged">Acknowledged</MenuItem>
                <MenuItem value="resolved">Resolved</MenuItem>
                <MenuItem value="dismissed">Dismissed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Severity</InputLabel>
              <Select
                value={filters.severity || ''}
                label="Severity"
                onChange={(e) => setFilters({ ...filters, severity: e.target.value as any })}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="critical">Critical</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="low">Low</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="PO Number"
              value={filters.po_number || ''}
              onChange={(e) => setFilters({ ...filters, po_number: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadAlerts}
                disabled={loading}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                onClick={handleGenerateAlerts}
                disabled={loading}
              >
                Scan for New Alerts
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Alerts Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>Severity</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>PO Number</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {alerts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="textSecondary">No alerts found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              alerts.map((alert) => (
                <TableRow key={alert.id} hover>
                  <TableCell>
                    <Chip
                      label={getAlertTypeLabel(alert.alert_type)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={getSeverityIcon(alert.severity)}
                      label={alert.severity}
                      color={getSeverityColor(alert.severity)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{alert.title}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {alert.description.substring(0, 100)}...
                    </Typography>
                  </TableCell>
                  <TableCell>{alert.po_number || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={alert.status}
                      size="small"
                      color={alert.status === 'resolved' ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {new Date(alert.created_at).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {alert.status === 'open' && (
                        <Tooltip title="Acknowledge">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedAlert(alert);
                              setActionDialog('acknowledge');
                            }}
                          >
                            <AcknowledgeIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {(alert.status === 'open' || alert.status === 'acknowledged') && (
                        <>
                          <Tooltip title="Resolve">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => {
                                setSelectedAlert(alert);
                                setActionDialog('resolve');
                              }}
                            >
                              <ResolveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Dismiss">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                setSelectedAlert(alert);
                                setActionDialog('dismiss');
                              }}
                            >
                              <DismissIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Acknowledge Dialog */}
      <Dialog open={actionDialog === 'acknowledge'} onClose={() => setActionDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Acknowledge Alert</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            {selectedAlert?.title}
          </Typography>
          <TextField
            fullWidth
            label="Assign To (Optional)"
            value={assignTo}
            onChange={(e) => setAssignTo(e.target.value)}
            helperText="Enter staff number to assign this alert"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialog(null)}>Cancel</Button>
          <Button onClick={handleAcknowledge} variant="contained">
            Acknowledge
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resolve Dialog */}
      <Dialog open={actionDialog === 'resolve'} onClose={() => setActionDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Resolve Alert</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            {selectedAlert?.title}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Resolution Notes"
            value={actionNotes}
            onChange={(e) => setActionNotes(e.target.value)}
            required
            helperText="Describe how this issue was resolved"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialog(null)}>Cancel</Button>
          <Button
            onClick={handleResolve}
            variant="contained"
            color="success"
            disabled={!actionNotes.trim()}
          >
            Resolve
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dismiss Dialog */}
      <Dialog open={actionDialog === 'dismiss'} onClose={() => setActionDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Dismiss Alert</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            {selectedAlert?.title}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Dismissal Reason"
            value={actionNotes}
            onChange={(e) => setActionNotes(e.target.value)}
            required
            helperText="Explain why this alert is not an issue"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialog(null)}>Cancel</Button>
          <Button
            onClick={handleDismiss}
            variant="contained"
            color="error"
            disabled={!actionNotes.trim()}
          >
            Dismiss
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AlertsDashboard;
